import React, { useEffect, useMemo, useRef, useState } from "react"
import PropTypes from "prop-types"
import { Modal, ModalBody } from "reactstrap"
import { useFormik } from "formik"
import * as yup from "yup"
import { toast, Bounce } from "react-toastify"
import { useSelector } from "react-redux"
import Select from "react-select"
import { components as selectComponents } from "react-select"
import CustomInput from "../OnboardingComponents/CustomInput"
import { DateField } from "../OnboardingComponents/CustomDatePicker"
import CustomButton from "../OnboardingComponents/CustomButton"
import { Avatar } from "../DashboardComponents/AvatarGroup"
import { ICONS } from "../OnboardingComponents/icon"
import { ICONS as APP_ICONS } from "../../constants/icons"
import { post } from "../../api/axios"
import endpoints from "../../api/endpoints"
import { toastApiError } from "../../helpers/apiError"
import { getUserAvatarUrl } from "../../helpers/userAvatar"
import { withYouLabel } from "../../helpers/userDisplay"

const WEEKDAY_BY_INDEX = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
]

const WEEKDAY_LABELS = {
  sunday: "Sunday",
  monday: "Monday",
  tuesday: "Tuesday",
  wednesday: "Wednesday",
  thursday: "Thursday",
  friday: "Friday",
  saturday: "Saturday",
}

const toDateOnly = value => {
  if (!value) return ""
  const match = String(value).match(/^(\d{4}-\d{2}-\d{2})/)
  return match ? match[1] : ""
}

/** Local calendar date as YYYY-MM-DD (avoids UTC off-by-one from toISOString). */
const todayIso = () => {
  const d = new Date()
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, "0")
  const day = String(d.getDate()).padStart(2, "0")
  return `${y}-${m}-${day}`
}

/** Later of two YYYY-MM-DD strings; empty values are ignored. */
const laterDate = (a, b) => {
  const left = toDateOnly(a)
  const right = toDateOnly(b)
  if (!left) return right
  if (!right) return left
  return left >= right ? left : right
}

const weekdayKeyFromIsoDate = isoDate => {
  const dateOnly = toDateOnly(isoDate)
  if (!dateOnly) return null
  const [year, month, day] = dateOnly.split("-").map(Number)
  if (!year || !month || !day) return null
  return WEEKDAY_BY_INDEX[new Date(year, month - 1, day).getDay()] || null
}

const isTimeString = value => typeof value === "string" && value.length === 5

/** Format HH:mm (24h) as 12-hour label, e.g. "08:00" → "8:00 AM". */
const formatTime12Hour = value => {
  if (!isTimeString(value)) return value || ""
  const [hourStr, minuteStr] = value.split(":")
  const hour24 = Number(hourStr)
  const period = hour24 >= 12 ? "PM" : "AM"
  const hour12 = hour24 % 12 || 12
  return `${hour12}:${minuteStr} ${period}`
}

/** Decimal hours from HH:mm start/end (0 when invalid or end ≤ start). */
const hoursFromTimes = (startTime, endTime) => {
  if (!isTimeString(startTime) || !isTimeString(endTime)) return 0
  const [startHour, startMinute] = startTime.split(":").map(Number)
  const [endHour, endMinute] = endTime.split(":").map(Number)
  const diffMinutes = endHour * 60 + endMinute - (startHour * 60 + startMinute)
  if (diffMinutes <= 0) return 0
  return Math.round((diffMinutes / 60) * 100) / 100
}

const TIME_STEP_MINUTES = 15
const DEFAULT_START_TIME = "08:00"
const DEFAULT_END_TIME = "17:00"

const TIME_OPTIONS = (() => {
  const options = []
  for (let minutes = 0; minutes < 24 * 60; minutes += TIME_STEP_MINUTES) {
    const hh = String(Math.floor(minutes / 60)).padStart(2, "0")
    const mm = String(minutes % 60).padStart(2, "0")
    const value = `${hh}:${mm}`
    options.push({ value, label: formatTime12Hour(value) })
  }
  return options
})()

const resolveTimeOption = value => {
  if (!isTimeString(value)) return null
  return (
    TIME_OPTIONS.find(option => option.value === value) || {
      value,
      label: formatTime12Hour(value),
    }
  )
}

/** First TIME_OPTIONS slot strictly after `time`, or "" if none. */
const nextTimeAfter = time => {
  if (!isTimeString(time)) return ""
  const idx = TIME_OPTIONS.findIndex(option => option.value === time)
  if (idx === -1) {
    const after = TIME_OPTIONS.find(option => option.value > time)
    return after?.value || ""
  }
  return TIME_OPTIONS[idx + 1]?.value || ""
}

const endTimeOptionsAfter = startTime => {
  if (!isTimeString(startTime)) return TIME_OPTIONS
  return TIME_OPTIONS.filter(option => option.value > startTime)
}

const scheduleTimeSelectStyles = (hasError = false) => ({
  control: (base, state) => ({
    ...base,
    minHeight: 38,
    height: 38,
    border: hasError ? "1px solid #9b0000" : "none",
    borderRadius: 12,
    backgroundColor: "#f3f3f5",
    boxShadow: "none",
    cursor: "pointer",
    opacity: state.isFocused ? 1 : 0.85,
    "&:hover": { borderColor: hasError ? "#9b0000" : "transparent" },
  }),
  valueContainer: base => ({
    ...base,
    padding: "0 8px",
    justifyContent: "flex-end",
  }),
  singleValue: base => ({
    ...base,
    margin: 0,
    color: "#0d0d0d",
    fontSize: 14,
    fontWeight: 500,
    textAlign: "right",
  }),
  input: base => ({
    ...base,
    margin: 0,
    color: "#0d0d0d",
    fontSize: 14,
  }),
  placeholder: base => ({
    ...base,
    margin: 0,
    color: "rgba(110, 110, 110, 0.7)",
    fontSize: 14,
  }),
  indicatorsContainer: base => ({
    ...base,
    height: 38,
  }),
  dropdownIndicator: base => ({
    ...base,
    padding: "0 6px 0 0",
    color: "#6e6e6e",
  }),
  indicatorSeparator: () => ({ display: "none" }),
  menu: base => ({
    ...base,
    marginTop: 4,
    borderRadius: 12,
    boxShadow: "0 8px 24px rgba(17, 17, 17, 0.12)",
    overflow: "hidden",
    zIndex: 10060,
  }),
  menuPortal: base => ({ ...base, zIndex: 10060 }),
  menuList: base => ({
    ...base,
    maxHeight: 220,
    padding: 4,
    overflowY: "auto",
    overscrollBehavior: "contain",
    WebkitOverflowScrolling: "touch",
  }),
  option: (base, state) => ({
    ...base,
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 500,
    backgroundColor: state.isSelected
      ? "#ecf0fa"
      : state.isFocused
        ? "rgba(13, 13, 13, 0.04)"
        : "transparent",
    color: "#0d0d0d",
    cursor: "pointer",
  }),
})

const ScheduleTimeSelect = ({
  value,
  onChange,
  ariaLabel,
  hasError = false,
  id,
  options = TIME_OPTIONS,
}) => (
  <Select
    inputId={id}
    className="amok-modal__time-select"
    classNamePrefix="amok-modal-time"
    options={options}
    value={resolveTimeOption(value)}
    onChange={option => onChange(option?.value || "")}
    isSearchable
    menuPlacement="auto"
    menuPosition="fixed"
    menuShouldScrollIntoView
    maxMenuHeight={220}
    menuPortalTarget={typeof document !== "undefined" ? document.body : null}
    styles={scheduleTimeSelectStyles(hasError)}
    aria-label={ariaLabel}
    placeholder="8:00 AM"
    components={{ IndicatorSeparator: null }}
  />
)

ScheduleTimeSelect.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  ariaLabel: PropTypes.string,
  hasError: PropTypes.bool,
  id: PropTypes.string,
  options: PropTypes.array,
}

const INITIAL_VALUES = {
  title: "",
  description: "",
  dueDate: "",
  startTime: DEFAULT_START_TIME,
  endTime: DEFAULT_END_TIME,
  hours: hoursFromTimes(DEFAULT_START_TIME, DEFAULT_END_TIME),
  collaborators: [],
}

const optionMatchesUser = (option, userId) => {
  if (!userId || !option) return false
  // Prefer explicit userId so option.value can safely be the project member id.
  if (option.userId) {
    return String(option.userId) === String(userId)
  }
  return String(option.value || "") === String(userId)
}

const mapCurrentUserToOption = (user, memberId = null) => {
  if (!user?.id || !memberId) return null
  const name =
    `${user.first_name || ""} ${user.last_name || ""}`.trim() ||
    user.email ||
    "You"
  const email = user.email || ""
  const userId = String(user.id)

  return {
    value: String(memberId),
    label: email ? `${name} (${email})` : name,
    name: withYouLabel(name),
    email,
    avatar: getUserAvatarUrl(user),
    userId,
    isFixed: true,
  }
}

const lockCurrentUserOption = (option, currentUserId) => {
  if (!optionMatchesUser(option, currentUserId)) return option
  return {
    ...option,
    isFixed: true,
    name: withYouLabel(option.name),
  }
}

const ensureCurrentUserSelected = (
  selected,
  currentUserOption,
  currentUserId,
) => {
  const next = (selected || []).filter(Boolean)
  if (!currentUserOption) return next
  const others = next.filter(
    option => !optionMatchesUser(option, currentUserId),
  )
  return [currentUserOption, ...others]
}

const mapMemberToOption = member => {
  const user = member?.user || {}
  const name =
    member?.name ||
    `${user.first_name || ""} ${user.last_name || ""}`.trim() ||
    user.full_name ||
    user.email ||
    member?.email ||
    "Collaborator"
  const email = user.email || member?.email || ""
  // Project member row id — required for collaborator_ids (not auth user id).
  const memberId = member?.id
  const userId =
    member?.userId || member?.user_id || user.id || member?.user?.id || ""

  if (!memberId) return null

  return {
    value: String(memberId),
    label: email ? `${name} (${email})` : name,
    name,
    email,
    avatar: member?.avatar || getUserAvatarUrl(user),
    userId: userId ? String(userId) : "",
  }
}

const CollaboratorOption = props => {
  const { data } = props
  return (
    <selectComponents.Option {...props}>
      <span className="project-collab-modal__option">
        <Avatar
          src={data.avatar}
          name={data.name}
          size="xs"
          className="project-collab-modal__option-avatar"
        />
        <span className="project-collab-modal__option-meta">
          <span className="project-collab-modal__option-name">{data.name}</span>
          {data.email ? (
            <span className="project-collab-modal__option-email">
              {data.email}
            </span>
          ) : null}
        </span>
      </span>
    </selectComponents.Option>
  )
}

const CollaboratorMultiValueLabel = props => {
  const { data } = props
  const chipKey = data?.value || data?.userId || data?.email || data?.name
  return (
    <selectComponents.MultiValueLabel {...props}>
      <span className="project-collab-modal__chip">
        <Avatar
          key={chipKey}
          src={data.avatar}
          name={data.name}
          size="xs"
          className="project-collab-modal__chip-avatar"
        />
        <span className="project-collab-modal__chip-name">{data.name}</span>
      </span>
    </selectComponents.MultiValueLabel>
  )
}

const collaboratorSelectStyles = (hasError = false) => ({
  control: (base, state) => ({
    ...base,
    minHeight: 30,
    border: "none",
    borderRadius: 12,
    backgroundColor: "transparent",
    boxShadow: "none",
    cursor: "text",
    paddingLeft: 0,
    borderColor: hasError ? "#9b0000" : "transparent",
    "&:hover": { border: "none", backgroundColor: "transparent" },
    ...(state.isFocused ? { outline: "none" } : {}),
  }),
  valueContainer: base => ({
    ...base,
    padding: "0",
    gap: 6,
    minHeight: 30,
  }),
  container: base => ({
    ...base,
    width: "100%",
  }),
  input: base => ({
    ...base,
    margin: 0,
    padding: 0,
    color: "#0d0d0d",
    fontSize: 14,
    fontWeight: 500,
  }),
  placeholder: base => ({
    ...base,
    margin: 0,
    color: "rgba(110, 110, 110, 0.7)",
    fontSize: 14,
    fontWeight: 500,
  }),
  singleValue: base => ({
    ...base,
    margin: 0,
    color: "#0d0d0d",
    fontSize: 14,
    fontWeight: 500,
  }),
  multiValue: base => ({
    ...base,
    margin: 0,
    backgroundColor: "#ecf0fa",
    borderRadius: 999,
  }),
  multiValueLabel: base => ({
    ...base,
    padding: "2px 4px 2px 2px",
  }),
  multiValueRemove: (base, state) => ({
    ...base,
    ...(state.data?.isFixed ? { display: "none" } : {}),
    borderRadius: 999,
    color: "#6e6e6e",
    cursor: "pointer",
    ":hover": {
      backgroundColor: "rgba(155, 0, 0, 0.08)",
      color: "#9b0000",
    },
  }),
  clearIndicator: () => ({ display: "none" }),
  dropdownIndicator: () => ({ display: "none" }),
  indicatorSeparator: () => ({ display: "none" }),
  menu: base => ({
    ...base,
    marginTop: 8,
    borderRadius: 12,
    boxShadow: "0 8px 24px rgba(17, 17, 17, 0.08)",
    overflow: "hidden",
    zIndex: 10060,
  }),
  menuPortal: base => ({ ...base, zIndex: 10060 }),
  menuList: base => ({
    ...base,
    padding: 4,
    maxHeight: 220,
  }),
  option: (base, state) => ({
    ...base,
    padding: "8px 10px",
    borderRadius: 8,
    backgroundColor:
      state.isFocused || state.isSelected ? "#f2f2f4" : "transparent",
    color: "#0d0d0d",
    cursor: "pointer",
    "&:active": { backgroundColor: "#e7e7ea" },
  }),
  noOptionsMessage: base => ({
    ...base,
    fontSize: 13,
    color: "#b7b7b7",
  }),
})

const isWithinProjectRange = (value, projectStart, projectEnd) => {
  if (!value) return true
  if (projectStart && value < projectStart) return false
  if (projectEnd && value > projectEnd) return false
  return true
}

const projectRangeMessage = (projectStart, projectEnd) => {
  if (projectStart && projectEnd) {
    return `Date must be on or between ${projectStart} and ${projectEnd}`
  }
  if (projectStart) {
    return `Date must be on or after ${projectStart}`
  }
  return "Date is outside the project range"
}

const buildValidationSchema = (projectStartDate, projectEndDate) => {
  const projectStart = toDateOnly(projectStartDate)
  const projectEnd = toDateOnly(projectEndDate)

  return yup.object().shape({
    title: yup
      .string()
      .trim()
      .required("Task title is required")
      .min(2, "Task title must be at least 2 characters")
      .max(120, "Task title must be less than 120 characters"),
    description: yup
      .string()
      .trim()
      .max(2000, "Description must be 2000 characters or less"),
    dueDate: yup
      .string()
      .required("Due date is required")
      .test("not-in-past", "Due date must be today or later", value => {
        if (!value) return true
        return value >= todayIso()
      })
      .test(
        "within-project",
        projectRangeMessage(projectStart, projectEnd),
        value => isWithinProjectRange(value, projectStart, projectEnd),
      ),
    startTime: yup
      .string()
      .nullable()
      .test("valid-start-time", "Start time must be a valid time", value =>
        !value || isTimeString(value),
      ),
    endTime: yup
      .string()
      .nullable()
      .test("valid-end-time", "End time must be a valid time", value =>
        !value || isTimeString(value),
      )
      .test(
        "is-after-start-time",
        "End time must be after start time",
        function (value) {
          const { startTime } = this.parent
          if (!isTimeString(startTime) || !isTimeString(value)) return true
          return value > startTime
        },
      ),
    hours: yup
      .number()
      .transform((value, originalValue) =>
        originalValue === "" ||
        originalValue === null ||
        originalValue === undefined
          ? undefined
          : Number(originalValue),
      )
      .nullable()
      .notRequired()
      .typeError("Hours must be a number")
      .min(0.5, "Hours must be at least 0.5"),
    collaborators: yup.array().of(yup.object()).nullable(),
  })
}

/**
 * CreateTaskModal
 * ------------------------------------------------------------------
 * Single-day Create Task modal. Same Custom* fields as
 * SetupWorkingScheduleModal (title, collaborators, description, date,
 * hours/start/end) without the date-range or recurring-day chips.
 */
const CreateTaskModal = ({
  isOpen,
  onClose,
  onSave = null,
  projectId = "",
  lockedDate = "",
  projectStartDate = "",
  projectEndDate = "",
  project = null,
}) => {
  const currentUser = useSelector(state => state.auth?.currentUser)
  const currentUserId = currentUser?.id != null ? String(currentUser.id) : ""
  const [collabMenuOpen, setCollabMenuOpen] = useState(false)
  const wasOpenRef = useRef(false)
  const lockedDueDate = toDateOnly(lockedDate)
  const isDateLocked = Boolean(lockedDueDate)
  const projectStart = toDateOnly(projectStartDate)
  const projectEnd = toDateOnly(projectEndDate)
  const projectMembers = project?.members
  const projectCollaborators = project?.collaborators

  const { collaboratorOptions, currentUserOption } = useMemo(() => {
    const list =
      Array.isArray(projectMembers) && projectMembers.length > 0
        ? projectMembers
        : projectCollaborators || []

    const options = list
      .map(mapMemberToOption)
      .filter(Boolean)
      .filter(option => option.value)
      .map(option => lockCurrentUserOption(option, currentUserId))

    const fromList = options.find(option =>
      optionMatchesUser(option, currentUserId),
    )

    // Always use project member id as value; enrich label/avatar from auth user
    // when create-project members have user_id but no nested user.
    let current = fromList
    if (currentUserId) {
      const rawMember = list.find(member => {
        const memberUserId =
          member?.userId ||
          member?.user_id ||
          member?.user?.id ||
          ""
        return String(memberUserId) === currentUserId && member?.id
      })
      if (rawMember?.id) {
        current = mapCurrentUserToOption(currentUser, rawMember.id) || fromList
      }
    }

    if (
      current &&
      !options.some(option => optionMatchesUser(option, currentUserId))
    ) {
      options.unshift(current)
    } else if (current) {
      const idx = options.findIndex(option =>
        optionMatchesUser(option, currentUserId),
      )
      if (idx >= 0) options[idx] = { ...options[idx], ...current, isFixed: true }
    }

    return { collaboratorOptions: options, currentUserOption: current }
  }, [projectMembers, projectCollaborators, currentUser, currentUserId])

  const validationSchema = useMemo(
    () => buildValidationSchema(projectStart, projectEnd),
    [projectStart, projectEnd],
  )

  const formik = useFormik({
    initialValues: INITIAL_VALUES,
    validationSchema,
    enableReinitialize: false,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: async (values, { setSubmitting }) => {
      if (!projectId) {
        toast.error("Project ID is missing. Please create the project first.", {
          position: "bottom-center",
          theme: "colored",
          transition: Bounce,
        })
        setSubmitting(false)
        return
      }

      const dueDate = toDateOnly(values.dueDate)
      const day = weekdayKeyFromIsoDate(dueDate)
      if (!dueDate || !day) {
        toast.error("Please select a valid due date.", {
          position: "bottom-center",
          theme: "colored",
          transition: Bounce,
        })
        setSubmitting(false)
        return
      }

      const payload = {
        title: values.title.trim(),
        start_date: dueDate,
        end_date: dueDate,
        working_days: [
          {
            day,
            start_time: values.startTime,
            end_time: values.endTime,
            hours: hoursFromTimes(values.startTime, values.endTime),
          },
        ],
      }

      const description = String(values.description || "").trim()
      if (description) {
        payload.description = description
      }

      const collaboratorIds = Array.from(
        new Set(
          ensureCurrentUserSelected(
            values.collaborators,
            currentUserOption,
            currentUserId,
          )
            .map(item => String(item.value || "").trim())
            .filter(Boolean),
        ),
      )
      if (collaboratorIds.length > 0) {
        payload.collaborator_ids = collaboratorIds
      }

      try {
        const response = await post(endpoints.createTask(projectId), payload)

        if (response?.data?.success) {
          toast.success(
            response?.data?.message || "Task created successfully",
            {
              position: "bottom-center",
              theme: "colored",
              transition: Bounce,
            },
          )
          onSave?.(response?.data?.result)
        } else {
          toastApiError(response, "Failed to create task")
        }
      } catch (error) {
        toastApiError(error, "Failed to create task. Please try again.")
      } finally {
        setSubmitting(false)
      }
    },
  })

  useEffect(() => {
    if (!isOpen) {
      wasOpenRef.current = false
      return
    }

    if (lockedDueDate) {
      if (lockedDueDate < todayIso()) {
        toast.error("You can only schedule tasks for today or a future date.", {
          position: "bottom-center",
          theme: "colored",
          transition: Bounce,
        })
        onClose?.()
        return
      }

      formik.resetForm({
        values: {
          ...INITIAL_VALUES,
          dueDate: lockedDueDate,
          collaborators: currentUserOption ? [currentUserOption] : [],
        },
      })
      setCollabMenuOpen(false)
      wasOpenRef.current = true
      return
    }

    if (!wasOpenRef.current) {
      formik.resetForm({
        values: {
          ...INITIAL_VALUES,
          collaborators: currentUserOption ? [currentUserOption] : [],
        },
      })
      setCollabMenuOpen(false)
    }
    wasOpenRef.current = true
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, lockedDueDate])

  useEffect(() => {
    if (!isOpen || !currentUserOption) return
    const selected = formik.values.collaborators || []
    if (selected.some(option => optionMatchesUser(option, currentUserId))) return
    formik.setFieldValue(
      "collaborators",
      ensureCurrentUserSelected(selected, currentUserOption, currentUserId),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, currentUserOption, currentUserId])

  const datePickerMin = laterDate(todayIso(), projectStart) || undefined
  const datePickerMax = projectEnd || undefined
  const dueDayKey = weekdayKeyFromIsoDate(formik.values.dueDate)
  const dueDayLabel = dueDayKey ? WEEKDAY_LABELS[dueDayKey] : ""

  const syncHoursFromTimes = (startTime, endTime) => {
    const hours = hoursFromTimes(startTime, endTime)
    formik.setFieldValue("hours", hours > 0 ? hours : "")
  }

  const handleStartTimeChange = next => {
    let endTime = formik.values.endTime
    if (isTimeString(endTime) && endTime <= next) {
      endTime = nextTimeAfter(next)
    }
    formik.setFieldValue("startTime", next)
    formik.setFieldValue("endTime", endTime)
    syncHoursFromTimes(next, endTime)
  }

  const handleEndTimeChange = next => {
    formik.setFieldValue("endTime", next)
    syncHoursFromTimes(formik.values.startTime, next)
  }

  const showTimeErrors = formik.submitCount > 0
  const scheduleErrors = showTimeErrors
    ? [
        formik.errors.startTime,
        formik.errors.endTime,
        isTimeString(formik.values.startTime) &&
        isTimeString(formik.values.endTime)
          ? formik.errors.hours
          : null,
      ].filter(Boolean)
    : []

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      centered
      className="amok-modal amok-modal--schedule"
      contentClassName="amok-modal__content"
      backdropClassName="amok-modal__backdrop amok-modal-backdrop--blur"
    >
      <ModalBody className="amok-modal__body">
        <form onSubmit={formik.handleSubmit} className="amok-modal__shell">
          <div className="amok-modal__main">
            <div className="amok-modal__header">
              <h2 className="amok-modal__title amok-modal__title--left">
                Create Task
              </h2>
              <p className="amok-modal__subtitle amok-modal__subtitle--left">
                Create a single-day task with a title, due date, and working
                hours.
              </p>
            </div>

            <div className="amok-modal__form">
            <div className="amok-modal__section">
              <CustomInput
                label="Task Title"
                placeholder="Enter your task title"
                name="title"
                value={formik.values.title}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.title && formik.errors.title}
                required
              />
            </div>

            <div className="amok-modal__section">
              <div className="amok-modal__field-label-row">
                <label
                  className="amok-modal__field-label"
                  htmlFor="create-task-collab-search"
                >
                  Search Collaborator
                </label>
                <span className="amok-modal__optional">(optional)</span>
              </div>
              <div className="project-collab-modal__search amok-modal__collab-search">
                <img src={APP_ICONS.messageSearchCollab} alt="" />
                <Select
                  inputId="create-task-collab-search"
                  className="project-collab-modal__user-select"
                  classNamePrefix="project-collab-modal-user"
                  options={collaboratorOptions}
                  value={formik.values.collaborators}
                  onChange={selected =>
                    formik.setFieldValue(
                      "collaborators",
                      ensureCurrentUserSelected(
                        selected,
                        currentUserOption,
                        currentUserId,
                      ),
                    )
                  }
                  onBlur={() => formik.setFieldTouched("collaborators", true)}
                  isMulti
                  isSearchable
                  isClearable={false}
                  getOptionValue={option =>
                    String(option.value || option.userId || "")
                  }
                  closeMenuOnSelect={false}
                  hideSelectedOptions
                  menuIsOpen={collabMenuOpen}
                  onMenuOpen={() => setCollabMenuOpen(true)}
                  onMenuClose={() => setCollabMenuOpen(false)}
                  placeholder="Search by email or username..."
                  noOptionsMessage={() => "No collaborators found"}
                  components={{
                    Option: CollaboratorOption,
                    MultiValueLabel: CollaboratorMultiValueLabel,
                    IndicatorSeparator: null,
                    DropdownIndicator: null,
                  }}
                  styles={collaboratorSelectStyles()}
                  menuPortalTarget={
                    typeof document !== "undefined" ? document.body : null
                  }
                  menuPosition="fixed"
                  filterOption={(option, rawInput) => {
                    const query = rawInput.trim().toLowerCase()
                    if (!query) return true
                    const haystack = `${option.label} ${
                      option.data?.email || ""
                    } ${option.data?.name || ""}`.toLowerCase()
                    return haystack.includes(query)
                  }}
                  aria-label="Search Collaborator"
                />
              </div>
            </div>

            <div className="amok-modal__section">
              <div className="amok-modal__field-label-row">
                <span className="amok-modal__field-label">
                  Task Description
                </span>
                <span className="amok-modal__optional">(optional)</span>
              </div>
              <CustomInput
                placeholder="Describe the task in detail"
                name="description"
                textarea
                rows={3}
                value={formik.values.description}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.description && formik.errors.description}
              />
            </div>

            <div className="amok-modal__section">
              <div className="amok-modal__date-field">
                <span className="amok-modal__field-label">Due Date *</span>
                <DateField
                  label="Select date"
                  value={formik.values.dueDate}
                  onChange={e =>
                    formik.setFieldValue("dueDate", e.target.value)
                  }
                  onBlur={() => formik.setFieldTouched("dueDate", true)}
                  calendarIconSrc={ICONS.calendar}
                  disabled={isDateLocked}
                  min={datePickerMin}
                  max={datePickerMax}
                  hasError={formik.touched.dueDate && !!formik.errors.dueDate}
                />
                {formik.touched.dueDate && formik.errors.dueDate && (
                  <p className="amok-field__message amok-field__message--error">
                    {formik.errors.dueDate}
                  </p>
                )}
              </div>
            </div>

            {formik.values.dueDate ? (
              <div className="amok-modal__table-wrapper">
                <div className="amok-modal__table">
                  <div className="amok-modal__table-head">
                    <span>Day</span>
                    <span className="amok-modal__table-metrics amok-modal__table-metrics--head">
                      <span>Hours</span>
                      <span>Start Time</span>
                      <span>End Time</span>
                    </span>
                  </div>
                  <div className="amok-modal__table-row">
                    <span className="amok-modal__table-day">{dueDayLabel}</span>
                    <div className="amok-modal__table-metrics">
                      <div className="amok-modal__hours amok-modal__hours--disabled">
                        <input
                          type="number"
                          min="0"
                          step="0.5"
                          className="amok-modal__number"
                          name="hours"
                          value={formik.values.hours}
                          disabled
                          readOnly
                          aria-invalid={showTimeErrors && !!formik.errors.hours}
                          aria-label={`${dueDayLabel} hours`}
                        />
                        <span className="amok-modal__suffix">hrs</span>
                      </div>
                      <ScheduleTimeSelect
                        id="create-task-start-time"
                        value={formik.values.startTime}
                        onChange={handleStartTimeChange}
                        hasError={
                          showTimeErrors && !!formik.errors.startTime
                        }
                        ariaLabel={`${dueDayLabel} start time`}
                      />
                      <ScheduleTimeSelect
                        id="create-task-end-time"
                        value={formik.values.endTime}
                        options={endTimeOptionsAfter(formik.values.startTime)}
                        onChange={handleEndTimeChange}
                        hasError={showTimeErrors && !!formik.errors.endTime}
                        ariaLabel={`${dueDayLabel} end time`}
                      />
                    </div>
                    {scheduleErrors.length > 0 && (
                      <p className="amok-field__message amok-field__message--error amok-modal__table-row-error">
                        {scheduleErrors.join(". ")}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="amok-modal__section amok-modal__schedule-placeholder">
                <span className="amok-modal__field-label">Working Hours</span>
                <p className="amok-modal__schedule-placeholder-text">
                  Select a due date first to enter working hours.
                </p>
              </div>
            )}
            </div>
          </div>

          <div className="amok-modal__footer">
            <CustomButton
              variant="danger"
              type="button"
              className="amok-modal__btn"
              onClick={onClose}
              disabled={formik.isSubmitting}
            >
              Cancel
            </CustomButton>
            <CustomButton
              variant="primary"
              type="submit"
              className="amok-modal__btn"
              loading={formik.isSubmitting}
              disabled={formik.isSubmitting}
            >
              Create Task
            </CustomButton>
          </div>
        </form>
      </ModalBody>
    </Modal>
  )
}

CreateTaskModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func,
  projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  lockedDate: PropTypes.string,
  projectStartDate: PropTypes.string,
  projectEndDate: PropTypes.string,
  project: PropTypes.object,
}

export default CreateTaskModal
